import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Calendar, MapPin, Users, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

const DEFAULT_EVENT_IMAGE = 'https://customer-assets.emergentagent.com/job_creator-signup-4/artifacts/7bii36vj_ChatGPT%20Image%20Mar%2031%2C%202026%2C%2008_44_35%20PM.png';

export default function EventCard({ event, variant = 'default' }) {
  const availableTickets = event.total_tickets - event.sold_tickets;
  const isSoldOut = availableTickets === 0;
  const isHot = event.sold_tickets > 0 && (event.sold_tickets / event.total_tickets) > 0.7;
  
  const lowestPrice = event.ticket_tiers?.length > 0 
    ? Math.min(...event.ticket_tiers.map(t => t.price))
    : null;

  const hasAuction = event.ticket_tiers?.some(t => t.sale_mode === 'auction');

  if (variant === 'compact') {
    return (
      <Link 
        to={`/events/${event.id}`}
        className="flex items-center gap-4 p-3 rounded-xl bg-white/5 border border-white/10 hover:border-primary/50 hover:bg-white/10 transition-all group"
        data-testid={`event-card-compact-${event.id}`}
      >
        <img 
          src={event.image_url || DEFAULT_EVENT_IMAGE}
          alt={event.title}
          className="w-16 h-16 rounded-lg object-cover"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-semibold text-sm truncate group-hover:text-primary transition-colors">
            {event.title}
          </h4>
          <p className="text-xs text-muted-foreground truncate">{event.influencer_name}</p>
          {event.event_date && (
            <p className="text-xs text-muted-foreground mt-1">
              {format(new Date(event.event_date), 'MMM d, h:mm a')}
            </p>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link 
      to={`/events/${event.id}`}
      className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:border-primary/50 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1 duration-300"
      data-testid={`event-card-${event.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={event.image_url || DEFAULT_EVENT_IMAGE}
          alt={event.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {isHot && !isSoldOut && (
            <Badge className="bg-orange-500/90 text-white border-0 gap-1">
              <TrendingUp className="w-3 h-3" />
              Hot
            </Badge>
          )}
          {isSoldOut && (
            <Badge variant="destructive">Sold Out</Badge>
          )}
          {hasAuction && (
            <Badge className="bg-purple-500/90 text-white border-0">Auction</Badge>
          )}
        </div>

        {/* Category */}
        <div className="absolute top-3 right-3">
          <Badge variant="secondary" className="capitalize">
            {event.category}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <div>
          <h3 className="font-heading font-semibold text-lg line-clamp-1 group-hover:text-primary transition-colors">
            {event.title}
          </h3>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
            with <span className="text-foreground font-medium">{event.influencer_name}</span>
          </p>
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {event.event_date && (
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {format(new Date(event.event_date), 'MMM d')}
            </span>
          )}
          {event.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{event.location}</span>
            </span>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-white/10">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>{availableTickets > 0 ? `${availableTickets} spots left` : 'Sold out'}</span>
          </div>
          {lowestPrice !== null && (
            <div className="text-right">
              <span className="text-xs text-muted-foreground">from</span>
              <span className="ml-1 font-heading font-bold text-lg text-primary">
                ${lowestPrice}
              </span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
